/**
 * A chip, at one of three sizes.
 *
 * The art itself lives in `sigils.tsx` and the frame in `cards.css`; this file
 * is only the join between them and the game's data. It stays deliberately thin
 * so the visual system can be reworked without touching a single rule.
 *
 * Note there is not one inline `style=` attribute anywhere in here, and there
 * must never be: the CSP ships without `style-src-attr 'unsafe-inline'`, so an
 * inline style is not "slightly off-house-style", it silently does nothing.
 */

import { CHIPS, type ChipId } from '@shared/chips';
import { SIGILS } from './sigils';

export type ChipSize = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<ChipSize, string> = {
  sm: 'chip--sm',
  md: 'chip--md',
  lg: 'chip--lg',
};

export interface ChipProps {
  id: ChipId;
  size?: ChipSize;
  selected?: boolean;
  disabled?: boolean;
  /** A gold pulse inviting a tap. Used only where a tap is actually legal. */
  targetable?: boolean;
  /** Galahad's fist: this chip cannot be touched this round. */
  protectedChip?: boolean;
  /** Show the rules text. Off at `sm`, where it would be unreadable anyway. */
  showText?: boolean;
  /** Show the "×7" box-count badge. Useful while learning the deck. */
  showCount?: boolean;
  onClick?: () => void;
  /** Overrides the accessible name, e.g. "Sir Kay played Merlin". */
  label?: string;
}

export function Chip({
  id,
  size = 'sm',
  selected,
  disabled,
  targetable,
  protectedChip,
  showText,
  showCount,
  onClick,
  label,
}: ChipProps) {
  const def = CHIPS[id];
  const isSword = def.gender === 'sword';
  const classes = [
    'chip',
    SIZE_CLASS[size],
    isSword ? 'chip--sword' : '',
    id === 'cursedBlade' ? 'chip--cursed' : '',
    id === 'excalibur' ? 'chip--excalibur' : '',
    selected ? 'chip--selected' : '',
    disabled ? 'chip--disabled' : '',
    targetable ? 'chip--targetable' : '',
    protectedChip ? 'chip--protected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <span className="chip__sigil" aria-hidden="true">
        <svg viewBox="0 0 100 100" role="presentation" focusable="false">
          {SIGILS[id]}
        </svg>
      </span>
      <span className="chip__name">{size === 'sm' ? (def.short ?? def.name) : def.name}</span>
      {showText ? <span className="chip__text">{def.text}</span> : null}
      {showCount ? <span className="chip__count">{def.count} in the box</span> : null}
    </>
  );

  // A chip that does nothing is not a button. Screen readers announce a list of
  // eight buttons that cannot be pressed as eight dead ends.
  if (!onClick) {
    return (
      <span className={classes} role="img" aria-label={label ?? accessibleName(id, protectedChip)}>
        {body}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected ? true : undefined}
      aria-label={label ?? accessibleName(id, protectedChip)}
    >
      {body}
    </button>
  );
}

function accessibleName(id: ChipId, isProtected?: boolean): string {
  const def = CHIPS[id];
  // The ability text is the whole content of a chip, so it belongs in the name
  // rather than a title attribute nobody on a phone can surface.
  return `${def.name}. ${def.text}${isProtected ? ' Protected this round.' : ''}`;
}

/**
 * The facedown back. Avalon is a pile of these, and so is every opponent's hand,
 * so it is on screen more than any single character.
 */
export function ChipBack({ size = 'sm', label }: { size?: ChipSize; label?: string }) {
  // No sigil: the back is the lattice pattern painted by `.chip--back`. Putting a
  // character's emblem here — even a decorative one — would be a tell.
  return (
    <span className={`chip ${SIZE_CLASS[size]} chip--back`} role="img" aria-label={label ?? 'A facedown chip'} />
  );
}
